import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Divider, Input, useMediaQuery } from '@mui/material';
import { WithdrawPayload } from '@monorepo/client-types';
import { Stack, Typography, useTheme } from '@monorepo/nova-ui';

interface WithdrawalAmountSectionProps {
  form: UseFormReturn<WithdrawPayload>;
  maxWithdrawalLimit: number;
  whatUserGets: number;
  maximumWithdrawalMessage: string;
  handleMinimumValidation: (value: number) => string | true;
  userPayoutPercentage: number;
  isAffiliate: boolean;
}

export const WithdrawalAmountSection: React.FC<
  WithdrawalAmountSectionProps
> = ({
  maxWithdrawalLimit,
  whatUserGets,
  form,
  userPayoutPercentage,
  handleMinimumValidation,
  isAffiliate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['withdrawFundsNova']);
  const MIN_WITHDRAW = 150;

  // Extract common sections
  const availableProfitSection = (
    <Stack
      flex={1}
      height={isAffiliate && !isMobile ? theme.spacing(21.5) : 'auto'}
      justifyContent={isAffiliate ? 'space-between' : 'flex-start'}
      alignItems={isAffiliate && isMobile ? 'center' : 'flex-start'}
      gap={isAffiliate && isMobile ? 2 : 0}
    >
      <Typography variant={'body2'} color={theme.palette.text.secondary}>
        {isAffiliate
          ? t('withdrawFundsNova:available_credit')
          : t('withdrawFundsNova:available_profit')}
      </Typography>
      <Typography
        variant={'h4'}
        fontStyle={isAffiliate && isMobile ? 'normal' : 'italic'}
        color={theme.palette.secondaryInvert.main}
      >
        ${maxWithdrawalLimit.toFixed(2)}
      </Typography>
    </Stack>
  );

  const profitShareSection = (
    <Stack flex={1}>
      <Typography variant={'body2'} color={theme.palette.text.secondary}>
        {t('withdrawFundsNova:profit_share')}
      </Typography>
      <Typography
        color={theme.palette.text.secondary}
        variant={'h5'}
        fontWeight={'regular'}
        fontStyle={'italic'}
      >
        {userPayoutPercentage}%
      </Typography>
    </Stack>
  );

  const profitShareVerticalDivider = (
    <Divider
      orientation="vertical"
      flexItem
      sx={{
        margin: '0 10px',
        ...(isMobile ? {} : { height: 86 }),
      }}
    />
  );

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems={isMobile ? 'flex-start' : 'center'}
      justifyContent="space-between"
      bgcolor={theme.palette.background.contrast}
      borderRadius={4}
      padding={5}
      gap={isMobile ? 4 : 0}
      width="100%"
    >
      {isMobile ? (
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {availableProfitSection}
          {!isAffiliate && profitShareVerticalDivider}
          {!isAffiliate && profitShareSection}
        </Stack>
      ) : (
        <>
          {availableProfitSection}
          {!isAffiliate && profitShareVerticalDivider}
          {!isAffiliate && profitShareSection}
        </>
      )}
      {isMobile || (
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            marginInline: isAffiliate ? theme.spacing(4) : theme.spacing(2.5),
          }}
        />
      )}
      <Stack
        flex={1}
        width={isAffiliate && isMobile ? '100%' : 'auto'}
        height={isAffiliate && !isMobile ? theme.spacing(21.5) : 'auto'}
        justifyContent={isAffiliate ? 'space-between' : 'flex-start'}
        alignItems={isAffiliate && isMobile ? 'center' : 'flex-start'}
      >
        <Typography variant={'body2'} color={theme.palette.text.secondary}>
          {t('withdrawFundsNova:amount_to_withdraw')}
        </Typography>

        {!isAffiliate && (
          <Typography
            variant={'caption'}
            fontWeight={theme.typography.fontWeightRegular}
            color={theme.palette.text.secondary}
            sx={{
              whiteSpace: 'nowrap',
            }}
          >
            {t('withdrawFundsNova:your_profit_share_after_split')}{' '}
            <Typography
              display={'inline'}
              variant={'caption'}
              color={theme.palette.text.secondary}
              fontWeight={theme.typography.fontWeightMedium}
            >
              ${whatUserGets}
            </Typography>
          </Typography>
        )}

        <Stack direction={'row'} alignItems={'end'} gap={1}>
          <Typography
            color={theme.palette.text.secondary}
            variant={'h5'}
            fontWeight={'regular'}
            fontStyle={'italic'}
            marginBottom={isMobile ? (isAffiliate ? 0.5 : 2) : 1}
          >
            $
          </Typography>
          <Controller
            rules={{
              required: true,
              max: maxWithdrawalLimit,
              validate: handleMinimumValidation,
            }}
            name={'amount'}
            control={form.control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                type={'number'}
                error={!!fieldState.error}
                inputProps={{
                  sx: {
                    width: isAffiliate ? theme.spacing(55) : 'auto',
                    textAlign: isAffiliate && isMobile ? 'center' : 'left',
                    fontSize: isAffiliate
                      ? {
                          xs: theme.typography.h6.fontSize,
                          sm: theme.typography.h5.fontSize,
                        }
                      : theme.typography.h5.fontSize,
                    fontStyle: 'italic',
                    color: theme.palette.text.secondary,
                    marginRight: isAffiliate && isMobile ? theme.spacing(4) : 0,
                    paddingBottom: 0, // Hide number input arrows/spinners
                    '&::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '&::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '&[type=number]': {
                      MozAppearance: 'textfield', // For Firefox
                    },
                  },
                  max: maxWithdrawalLimit,
                  min: MIN_WITHDRAW,
                }}
                onChange={(event) => {
                  const value = event.target.value;
                  // Allow empty string, otherwise convert to number
                  field.onChange(value === '' ? '' : Number(value));
                }}
                fullWidth
              />
            )}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
